import React, { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Container,
    Paper,
    Box,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Tab,
    Tabs,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Link,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    useTheme,
    useMediaQuery,
    TextField
} from "@mui/material";
import {
    doc,
    collection,
    getDocs,
    query,
    orderBy,
    where,
    Timestamp,
    limit,
    getDoc
} from "firebase/firestore";
import * as XLSX from "xlsx";
import {
    Settings as SettingsIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Schedule as ScheduleIcon,
    EventNote as EventNoteIcon,
    Warning as WarningIcon,
    Logout as LogoutIcon,
    BarChart as BarChartIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Menu as MenuIcon,
    LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import NotificationCenter from "../components/NotificationCenter";
import EmployeeManagement from "./EmployeeManagement"; // This would be a new component for the employees tab

function AdminDashboard({ user, orgId }) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [message, setMessage] = useState("");
    const [logs, setLogs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [stats, setStats] = useState({
        totalEmployees: 0,
        clockInsToday: 0,
        averageDuration: 0,
        lastWeekActivity: []
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [orgDetails, setOrgDetails] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
    // Determine current tab based on URL path
    useEffect(() => {
        const path = location.pathname;
        if (path === "/admin") setCurrentTab(0);
        else if (path === "/admin/employees") setCurrentTab(1);
        else if (path === "/admin/timelogs") setCurrentTab(2);
        else if (path === "/admin/timeoff") setCurrentTab(3);
        else if (path === "/admin/disputes") setCurrentTab(4);
    }, [location.pathname]);

    const handleTabChange = (event, newValue) => {
        switch (newValue) {
            case 0:
                navigate("/admin");
                break;
            case 1:
                navigate("/admin/employees");
                break;
            case 2:
                navigate("/admin/timelogs");
                break;
            case 3:
                navigate("/admin/timeoff");
                break;
            case 4:
                navigate("/admin/disputes");
                break;
            default:
                navigate("/admin");
        }
        setCurrentTab(newValue);
        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        window.location.href = "/login";
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const logsRef = collection(db, "organizations", orgId, "timeLogs");
            let q = query(logsRef, orderBy("timestamp", "desc"), limit(500));

            // If we have date filters, apply them to the query
            if (dateRange.startDate || dateRange.endDate) {
                if (dateRange.startDate) {
                    const startDate = new Date(dateRange.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    q = query(q, where("timestamp", ">=", Timestamp.fromDate(startDate)));
                }

                if (dateRange.endDate) {
                    const endDate = new Date(dateRange.endDate);
                    endDate.setHours(23, 59, 59, 999);
                    q = query(q, where("timestamp", "<=", Timestamp.fromDate(endDate)));
                }
            }

            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLogs(data);

            // Process data for stats
            calculateStats(data);
        } catch (err) {
            console.error("❌ Firestore fetch error:", err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEmployees(data);
            setStats(prevStats => ({
                ...prevStats,
                totalEmployees: data.length
            }));
        } catch (err) {
            console.error("❌ Error fetching employees:", err.message);
        }
    };

    const fetchOrgDetails = async () => {
        try {
            const orgRef = doc(db, "organizations", orgId);
            const snapshot = await getDoc(orgRef);
            if (snapshot.exists()) {
                setOrgDetails(snapshot.data());
            }
        } catch (err) {
            console.error("❌ Error fetching organization details:", err.message);
        }
    };

    const handleDateFilterChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Add this function to filter logs by date range
    const getFilteredLogs = () => {
        if (!dateRange.startDate && !dateRange.endDate) {
            return logs;
        }

        return logs.filter(log => {
            if (!log.timestamp) return false;

            const logDate = log.timestamp.toDate();

            // Check start date
            if (dateRange.startDate) {
                const startDate = new Date(dateRange.startDate);
                startDate.setHours(0, 0, 0, 0);
                if (logDate < startDate) return false;
            }

            // Check end date
            if (dateRange.endDate) {
                const endDate = new Date(dateRange.endDate);
                endDate.setHours(23, 59, 59, 999);
                if (logDate > endDate) return false;
            }

            return true;
        });
    };

    const calculateStats = (logData) => {
        // Count clock-ins for today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = Timestamp.fromDate(today);

        const clockInsToday = logData.filter(log =>
            log.type === "clockIn" &&
            log.timestamp &&
            log.timestamp.toDate() >= todayTimestamp.toDate()
        ).length;

        // Calculate average duration between clock-in and clock-out
        let totalDuration = 0;
        let pairCount = 0;
        const clockInMap = new Map();

        // First, map all clock-ins by user
        logData.forEach(log => {
            if (log.type === "clockIn" && log.email) {
                if (!clockInMap.has(log.email)) {
                    clockInMap.set(log.email, []);
                }
                clockInMap.get(log.email).push(log);
            }
        });

        // Then find matching clock-outs and calculate durations
        logData.forEach(log => {
            if (log.type === "clockOut" && log.email && clockInMap.has(log.email)) {
                const userClockIns = clockInMap.get(log.email);
                if (userClockIns.length > 0) {
                    // Find the most recent clock-in before this clock-out
                    const matchingClockIn = userClockIns
                        .filter(ci => ci.timestamp && log.timestamp &&
                            ci.timestamp.toDate() < log.timestamp.toDate())
                        .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())[0];

                    if (matchingClockIn) {
                        const durationMs = log.timestamp.toDate() - matchingClockIn.timestamp.toDate();
                        const durationHours = durationMs / (1000 * 60 * 60);
                        totalDuration += durationHours;
                        pairCount++;

                        // Remove this clock-in so it doesn't get matched again
                        const index = userClockIns.indexOf(matchingClockIn);
                        if (index > -1) {
                            userClockIns.splice(index, 1);
                        }
                    }
                }
            }
        });

        const averageDuration = pairCount > 0 ? (totalDuration / pairCount).toFixed(1) : 0;

        // Last week activity by day
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const activityByDay = Array(7).fill(0);

        logData.forEach(log => {
            if (log.timestamp && log.timestamp.toDate() >= oneWeekAgo) {
                const dayIndex = log.timestamp.toDate().getDay();
                activityByDay[dayIndex]++;
            }
        });

        const lastWeekActivity = activityByDay.map((count, index) => ({
            day: dayNames[index],
            count
        }));

        setStats({
            totalEmployees: employees.length,
            clockInsToday,
            averageDuration,
            lastWeekActivity
        });
    };
    const handleExport = () => {

        // Get filtered logs based on date range
        const filteredLogs = getFilteredLogs();
        // Create a map of email to name for quick lookups

        const employeeNameMap = new Map();
        employees.forEach(employee => {
            employeeNameMap.set(employee.email.toLowerCase(), employee.name);
        });

        // Function to get employee name from email
        const getEmployeeName = (email) => {
            const name = employeeNameMap.get(email.toLowerCase());
            return name || "Unknown"; // Fallback if name not found
        };

        // Process logs to pair clock-ins with clock-outs and calculate hours worked
        const processedData = [];
        const clockInMap = new Map(); // Map to store clock-ins by employee email and date

        // Sort logs by employee and timestamp
        const sortedLogs = [...filteredLogs].sort((a, b) => {
            if (a.email !== b.email) return a.email.localeCompare(b.email);
            return a.timestamp.toDate() - b.timestamp.toDate();
        });

        // Process logs to find matching clock-ins and clock-outs
        sortedLogs.forEach(log => {
            if (!log.timestamp || !log.email) return;

            const email = log.email;
            const timestamp = log.timestamp.toDate();
            const dateKey = timestamp.toDateString();

            if (log.type === "clockIn") {
                // Store this clock-in
                if (!clockInMap.has(email)) {
                    clockInMap.set(email, []);
                }
                clockInMap.get(email).push({ id: log.id, timestamp, dateKey });
            } else if (log.type === "clockOut") {
                // Find a matching clock-in
                const employeeClockIns = clockInMap.get(email) || [];
                const matchingClockInIndex = employeeClockIns.findIndex(ci => ci.dateKey === dateKey);

                if (matchingClockInIndex !== -1) {
                    const clockIn = employeeClockIns[matchingClockInIndex];
                    // Remove this clock-in from the map
                    employeeClockIns.splice(matchingClockInIndex, 1);

                    // Calculate hours worked
                    const hoursWorked = (timestamp - clockIn.timestamp) / (1000 * 60 * 60);

                    // Add to processed data
                    processedData.push({
                        Employee: getEmployeeName(email),
                        Email: email,
                        Date: clockIn.timestamp.toLocaleDateString(),
                        "Clock In": clockIn.timestamp.toLocaleTimeString(),
                        "Clock Out": timestamp.toLocaleTimeString(),
                        "Hours Worked": hoursWorked.toFixed(2)
                    });
                } else {
                    // No matching clock-in found, add as standalone clock-out
                    processedData.push({
                        Employee: getEmployeeName(email),
                        Email: email,
                        Date: timestamp.toLocaleDateString(),
                        "Clock In": "N/A",
                        "Clock Out": timestamp.toLocaleTimeString(),
                        "Hours Worked": "N/A"
                    });
                }
            }
        });

        // Add remaining clock-ins without matching clock-outs
        clockInMap.forEach((clockIns, email) => {
            clockIns.forEach(clockIn => {
                processedData.push({
                    Employee: getEmployeeName(email),
                    Email: email,
                    Date: clockIn.timestamp.toLocaleDateString(),
                    "Clock In": clockIn.timestamp.toLocaleTimeString(),
                    "Clock Out": "N/A",
                    "Hours Worked": "N/A"
                });
            });
        });

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(processedData);

        // Create workbook and add the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Time Logs");

        // Generate filename with timestamp
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, "-").split("T")[0];
        const filename = `time_logs_${timestamp}.xlsx`;

        // Write file and download
        XLSX.writeFile(workbook, filename);
    };

    useEffect(() => {
        fetchLogs();
        fetchEmployees();
        fetchOrgDetails();
    }, []);
    // Navigation drawer for mobile
    const renderDrawer = () => (
        <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
        >
            <Box sx={{ width: 250 }} role="presentation">
                <List>
                    <ListItem>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            TimeWarden
                        </Typography>
                    </ListItem>
                    <Divider />
                    <ListItem
                        button
                        onClick={() => handleTabChange(null, 0)}
                        selected={currentTab === 0}
                    >
                        <ListItemIcon>
                            <DashboardIcon color={currentTab === 0 ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Dashboard" />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => handleTabChange(null, 1)}
                        selected={currentTab === 1}
                    >
                        <ListItemIcon>
                            <PeopleIcon color={currentTab === 1 ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Employees" />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => handleTabChange(null, 2)}
                        selected={currentTab === 2}
                    >
                        <ListItemIcon>
                            <ScheduleIcon color={currentTab === 2 ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Time Logs" />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => handleTabChange(null, 3)}
                        selected={currentTab === 3}
                    >
                        <ListItemIcon>
                            <EventNoteIcon color={currentTab === 3 ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Time Off" />
                    </ListItem>
                    <ListItem
                        button
                        onClick={() => handleTabChange(null, 4)}
                        selected={currentTab === 4}
                    >
                        <ListItemIcon>
                            <WarningIcon color={currentTab === 4 ? "primary" : "inherit"} />
                        </ListItemIcon>
                        <ListItemText primary="Disputes" />
                    </ListItem>
                    <Divider />
                    <ListItem
                        button
                        onClick={() => navigate('/admin/settings')}
                    >
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Settings" />
                    </ListItem>
                    <ListItem
                        button
                        onClick={handleLogout}
                    >
                        <ListItemIcon>
                            <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                    </ListItem>
                </List>
            </Box>
        </Drawer>
    );
    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={() => setDrawerOpen(true)}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden {orgDetails?.name ? `- ${orgDetails.name}` : ''}
                    </Typography>
                    <NotificationCenter orgId={orgId} />
                    <IconButton
                        color="inherit"
                        onClick={() => navigate("/admin/settings")}
                        sx={{ mr: 1 }}
                    >
                        <SettingsIcon />
                    </IconButton>
                    <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </Toolbar>

                {!isMobile && (
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{ bgcolor: 'primary.dark' }}
                    >
                        <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
                        <Tab icon={<PeopleIcon />} label="Employees" iconPosition="start" />
                        <Tab icon={<ScheduleIcon />} label="Time Logs" iconPosition="start" />
                        <Tab icon={<EventNoteIcon />} label="Time Off" iconPosition="start" />
                        <Tab icon={<WarningIcon />} label="Disputes" iconPosition="start" />
                    </Tabs>
                )}
            </AppBar>

            {isMobile && renderDrawer()}
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                {/* DASHBOARD CONTENT */}
                {currentTab === 0 && (
                    <>
                        {/* Stats Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Total Employees
                                        </Typography>
                                        <Typography variant="h3">
                                            {stats.totalEmployees}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Clock-ins Today
                                        </Typography>
                                        <Typography variant="h3">
                                            {stats.clockInsToday}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <CardContent>
                                        <Typography color="text.secondary" gutterBottom>
                                            Avg. Shift Duration
                                        </Typography>
                                        <Typography variant="h3">
                                            {stats.averageDuration}h
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Analytics Charts */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>Weekly Activity</Typography>
                                    <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={stats.lastWeekActivity}
                                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <XAxis dataKey="day" />
                                                <YAxis />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar dataKey="count" name="Activity" fill="#8884d8" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>Clock In/Out Ratio</Typography>
                                    <Box sx={{ height: 300 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: 'Clock In', value: logs.filter(l => l.type === 'clockIn').length },
                                                        { name: 'Clock Out', value: logs.filter(l => l.type === 'clockOut').length }
                                                    ]}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    {[
                                                        { name: 'Clock In', value: logs.filter(l => l.type === 'clockIn').length },
                                                        { name: 'Clock Out', value: logs.filter(l => l.type === 'clockOut').length }
                                                    ].map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </>
                )}
                {/* EMPLOYEES TAB CONTENT */}
                {currentTab === 1 && (
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5">
                                <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                Employee Management
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={fetchEmployees}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Here you would include the extracted employee management functionality */}
                        <EmployeeManagement
                            user={user}
                            orgId={orgId}
                            employees={employees}
                            setEmployees={setEmployees}
                            loading={loading}
                            setLoading={setLoading}
                        />
                    </Paper>
                )}

                {/* TIME LOGS TAB CONTENT */}
                {currentTab === 2 && (
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5">
                                <ScheduleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                Employee Time Logs
                            </Typography>
                            <Box>
                                <Button
                                    variant="outlined"
                                    onClick={handleExport}
                                    startIcon={<DownloadIcon />}
                                    sx={{ mr: 2 }}
                                >
                                    Export to Excel
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchLogs}
                                    disabled={loading}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>

                        {/* Date Filters */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            <TextField
                                label="Start Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={dateRange.startDate || ''}
                                onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                                sx={{ width: 200 }}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={dateRange.endDate || ''}
                                onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                                sx={{ width: 200 }}
                            />
                            <Button
                                variant="outlined"
                                onClick={fetchLogs}
                                disabled={loading}
                            >
                                Apply Filter
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setDateRange({ startDate: null, endDate: null });
                                    fetchLogs();
                                }}
                                disabled={loading}
                            >
                                Clear Filters
                            </Button>
                        </Box>

                        {loading ? <CircularProgress /> : (
                            <>
                                {logs.length === 0 ? (
                                    <Alert severity="info">
                                        No time logs recorded yet. Employee activity will appear here.
                                    </Alert>
                                ) : (
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Action</TableCell>
                                                <TableCell>Timestamp</TableCell>
                                                <TableCell>Location</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {logs.map(log => (
                                                <TableRow key={log.id}>
                                                    <TableCell>{log.email}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={log.type === "clockIn" ? "Clock In" : "Clock Out"}
                                                            color={log.type === "clockIn" ? "success" : "error"}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.timestamp?.toDate().toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        {log.location ? (
                                                            <Link
                                                                href={`https://www.google.com/maps?q=${log.location.lat},${log.location.lng}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {log.location.lat.toFixed(6)}, {log.location.lng.toFixed(6)}
                                                            </Link>
                                                        ) : "Not available"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </>
                        )}
                    </Paper>
                )}
                {/* TIME OFF TAB CONTENT */}
                {currentTab === 3 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* DISPUTES TAB CONTENT */}
                {currentTab === 4 && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Container>
        </>
    );
}

export default AdminDashboard;
